import React from "react";
import { useParams } from "react-router-dom";
import { Grid, Container, IconButton, Typography, Box, useTheme } from "@mui/material";

import { useQueryWithAuth } from "src/graphql/instances";
import { useSettingsContext } from "src/components/settings";
import { useLocales } from "src/locales";
import { useLocation, useNavigate } from "react-router";

import { PATHS } from "src/routes/paths";

import Page from "src/components/Page";
import CustomBreadcrumbs from "src/components/custom-breadcrumbs/CustomBreadcrumbs";
import Iconify from "src/components/iconify/Iconify";
import { GET_USER_ORDER_ITEMS } from "src/graphql/queries/orders";

import HoldingFeeIcon from "src/assets/icons/booking/HoldingFeeIcon";
import MenuItemIcon from "src/assets/icons/booking/MenuItemIcon";
import TicketIcon from "src/assets/icons/booking/TicketIcon";
import useResponsive from "src/hooks/useResponsive";
import { currencyMap } from "src/utils/currencies";

export default function UserEventOrderDetails() {
    const { eventId, eventName } = useParams();
    const { themeStretch } = useSettingsContext();
    const { translate } = useLocales();
    const navigate = useNavigate();
    const theme = useTheme();

    const isMobile = useResponsive("down", "md")

    const location = useLocation();
    const reservationId = location.state?.reservationId || null;


    const {
        loading: orderDetailsLoading,
        error: orderDetailsError,
        data: orderDetailsData,
    } = useQueryWithAuth(GET_USER_ORDER_ITEMS, {
        variables: {
            eventId: eventId,
            reservationId: reservationId
        },
        fetchPolicy: "network-only",
    });

    return (
        <Page title={eventName}>
            <Container maxWidth={themeStretch ? false : "lg"}>
                <CustomBreadcrumbs
                    heading={eventName}
                    headingActions={
                        <IconButton
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                navigate(PATHS.user.tickets);
                            }}
                            sx={{
                                mb: 1,
                            }}
                        >
                            <Iconify icon="mdi:arrow-left" />
                        </IconButton>
                    }
                    links={[
                        {
                            name: translate("userTicketsPage.myAccount"),
                            href: PATHS.user.account,
                        },
                        {
                            name: translate("userTicketsPage.tickets"),
                            href: PATHS.user.tickets,
                        },
                        {
                            name: eventName,
                        },
                    ]}
                />
                <Box
                    sx={{
                        margin: "0px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%"
                    }}
                >
                    {orderDetailsData?.getActivityOrderItems
                        ?.map((order) => (
                            <Box sx={{width: isMobile ? "90%" : "50%", mb: 5}}>
                                <Box
                                    sx={{
                                        py: 1,
                                        mb: 1,
                                        display: 'flex',
                                        flexDirection: isMobile ? "column" : "row",
                                        gap: isMobile? 0 : 1
                                    }}
                                >
                                    <Typography variant="h6">
                                        {translate('eventTicketsTable.orderID')}:
                                    </Typography>
                                    <Typography variant="h6" color={theme.palette.primary.main}>
                                        #{order.id}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pb: 2 }}>
                                    <Typography variant="h6">
                                        {translate('bookings.items')}
                                    </Typography>
                                    <Typography variant="h6">
                                        {translate('checkout.price')}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, overflowY: "auto", px: 2 }}>
                                    {order.items.map(item => (
                                        <Box
                                            key={item.id}
                                            sx={{
                                                display: 'flex',
                                                mb: 1.5,
                                                gap: 1
                                            }}
                                        >
                                            <Box sx={{ flexShrink: 0 }}>
                                                {item.type === "TICKET" ? <TicketIcon/> : item.type === "RESERVATION" ? <HoldingFeeIcon /> : <MenuItemIcon />}
                                            </Box>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    overflowWrap: 'break-word',
                                                    whiteSpace: 'normal',
                                                    flex: 1,
                                                    minWidth: 0,
                                                    mt: 0.25
                                                }}
                                            >
                                                {item.type === "RESERVATION" ? `${translate('bookings.holdingFeeFor')} ` : ''}{item.name}
                                            </Typography>
                                            <Typography variant="body1" textTransform="uppercase" sx={{ flexShrink: 0, ml: 2, mt: 0.25 }}>{item.itemPrice ? `${item.quantity} × ${item.itemPrice} ${currencyMap[order.event.currency]}` : translate('eventManager.free')}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pt: 0.5 }}>
                                    <Typography variant="h6">
                                        TOTAL
                                    </Typography>
                                    <Typography variant="h6" textTransform="uppercase">
                                        {order.price ? `${order.price} ${currencyMap[order.event.currency]}` : translate('eventManager.free')}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                </Box>
            </Container>
        </Page>
    );
}
